import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Radio,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useQuery } from '@apollo/client';
import { GET_PRO } from '@gql/pros';

const JOB_CREDITS_KEYS = ['total_jobs_credits', 'classic_jobs_credits', 'premium_jobs_credits'];

interface JobOfferCreditsModalProps {
    open: boolean;
    proId?: string;
    saving?: boolean;
    onContinue: (featureId: string) => void;
    onClose: () => void;
}

const JobOfferCreditsModal: React.FC<JobOfferCreditsModalProps> = ({
    open,
    proId,
    saving,
    onContinue,
    onClose,
}) => {
    const theme = useTheme();
    const [selectedFeatureId, setSelectedFeatureId] = useState<string>('');

    const { data: proData, loading: proLoading } = useQuery(GET_PRO, {
        variables: { proId },
        skip: !proId || !open,
    });

    const credits = useMemo(() => {
        const pro = proData?.getPro;
        if (!pro) return [];

        const result: any[] = [];
        const seen = new Set<string>();

        // Collect from planCredits
        (pro.planCredits ?? []).forEach((plan: any) => {
            (plan.credits ?? []).forEach((credit: any) => {
                if (JOB_CREDITS_KEYS.includes(credit.key) && !seen.has(credit._id)) {
                    seen.add(credit._id);
                    result.push(credit);
                }
            });
        });

        // Collect from credits (user credits)
        (pro.credits ?? []).forEach((credit: any) => {
            if (JOB_CREDITS_KEYS.includes(credit.key) && !seen.has(credit._id)) {
                seen.add(credit._id);
                result.push(credit);
            }
        });

        return result;
    }, [proData]);

    // Auto-select if there is only one option
    useEffect(() => {
        if (credits.length === 1 && !selectedFeatureId) {
            setSelectedFeatureId(credits[0]._id);
        }
    }, [credits, selectedFeatureId]);

    const selectedCredit = credits.find((c) => c._id === selectedFeatureId);
    const remaining = selectedCredit
        ? selectedCredit.configuration.maximum === -1
            ? Infinity
            : selectedCredit.configuration.maximum - (selectedCredit.configuration.used ?? 0)
        : 0;
    const hasEnoughCredits = remaining > 0;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 600 }}>Sélectionner un crédit</DialogTitle>

            <DialogContent dividers>
                {proLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : credits.length === 0 ? (
                    <Typography color={theme.palette.text.secondary} textAlign="center" py={2}>
                        Aucun crédit d'offre d'emploi disponible pour ce professionnel.
                    </Typography>
                ) : (
                    <List disablePadding>
                        {credits.map((credit) => {
                            const used = credit.configuration.used ?? 0;
                            const max = credit.configuration.maximum;
                            const rem = max === -1 ? '∞' : max - used;
                            const isSelected = selectedFeatureId === credit._id;
                            return (
                                <ListItem key={credit._id} disablePadding sx={{ mb: 1 }}>
                                    <ListItemButton
                                        onClick={() => setSelectedFeatureId(credit._id)}
                                        selected={isSelected}
                                        sx={{
                                            borderRadius: 2,
                                            border: `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.border?.main ?? theme.palette.divider}`,
                                            backgroundColor: isSelected
                                                ? `${theme.palette.primary.main}14`
                                                : 'transparent',
                                        }}
                                    >
                                        <Radio
                                            checked={isSelected}
                                            sx={{ mr: 1, p: 0 }}
                                            color="primary"
                                        />
                                        <ListItemText
                                            primary={
                                                <Typography variant="body2" fontWeight={600}>
                                                    {credit.name || credit.key}
                                                </Typography>
                                            }
                                            secondary={
                                                <Typography variant="caption" color={theme.palette.text.secondary}>
                                                    {credit.planName
                                                        ? `Plan ${credit.planName} — `
                                                        : ''}
                                                    {used}/{max === -1 ? '∞' : max} utilisé(s) · {rem} restant(s)
                                                </Typography>
                                            }
                                        />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button color="secondary" variant="text" onClick={onClose} sx={{ mr: 'auto' }}>
                    Annuler
                </Button>
                <Button
                    variant="contained"
                    disabled={!selectedFeatureId || !hasEnoughCredits || saving || proLoading}
                    onClick={() => onContinue(selectedFeatureId)}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                    Continuer
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default JobOfferCreditsModal;
